import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import FishStockingService from '../services/FishStocking';

const REGION_LABELS = { northeast: 'NE', southeast: 'SE', southwest: 'SW', northwest: 'NW' };
const REGION_COLORS = { northeast: '#0d6efd', southeast: '#198754', southwest: '#fd7e14', northwest: '#6f42c1' };

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestamp(ts) {
  if (!ts) return 'Never';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function RegionBadge({ region }) {
  const color = REGION_COLORS[region] || '#6c757d';
  return (
    <span style={{
      backgroundColor: color, color: '#fff', padding: '2px 8px',
      borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold',
    }}>
      {REGION_LABELS[region] || region}
    </span>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: color || '#f8f9fa', borderRadius: '8px',
      padding: '12px 16px', textAlign: 'center', minWidth: '130px',
    }}>
      <div style={{ fontSize: '1.8em', fontWeight: 'bold' }}>{value}</div>
      <div style={{ fontSize: '0.8em', color: '#666' }}>{label}</div>
    </div>
  );
}

const CELL_STYLE = {
  wordWrap: 'break-word', overflowWrap: 'break-word',
  whiteSpace: 'normal', verticalAlign: 'middle', fontSize: '0.85em',
};

const FishStocking = () => {
  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [thisWeekCount, setThisWeekCount] = useState(0);
  const [mostActiveRegion, setMostActiveRegion] = useState('');
  const [latestDate, setLatestDate] = useState('');
  const [lastScraped, setLastScraped] = useState(null);
  const [filters, setFilters] = useState({ region: '', days: '14', search: '' });
  const [sortField, setSortField] = useState('report_date');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [scrapeLogs, setScrapeLogs] = useState([]);

  function loadReports() {
    setLoading(true);
    FishStockingService.getReports(filters).then((resp) => {
      setReports(resp.reports || []);
      setTotalCount(resp.total_count || 0);
      setFilteredCount(resp.filtered_count || 0);
      setThisWeekCount(resp.this_week_count || 0);
      setMostActiveRegion(resp.most_active_region || '');
      setLatestDate(resp.latest_report_date || '');
      setLastScraped(resp.last_scraped);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  function loadScrapeLog() {
    FishStockingService.getScrapeLog().then((resp) => {
      setScrapeLogs(resp.logs || []);
    });
  }

  useEffect(() => { loadReports(); }, [filters.region, filters.days]);

  function handleSearch() { loadReports(); }

  function handleScrape() {
    setScraping(true);
    FishStockingService.scrapeNow().then(() => {
      setTimeout(() => { setScraping(false); loadReports(); loadScrapeLog(); }, 10000);
    });
  }

  function handleSort(field) {
    if (sortField === field) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir(field === 'report_date' ? 'desc' : 'asc'); }
  }

  function sortedReports() {
    return [...reports].sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  function sortArrow(field) {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className="m-5">
      <div className="container-fluid">
        <BackButton />
        <h2 className="text-center">CPW Fish Stocking Report</h2>
        <p className="text-center text-muted" style={{ fontSize: '0.85em' }}>
          Catchable trout (~10") stocking data from Colorado Parks and Wildlife. Updated every Friday during fishing season.
        </p>

        {/* Summary Cards */}
        <div className="d-flex justify-content-center gap-3 mb-3">
          <SummaryCard label="Total Stocked" value={totalCount} />
          <SummaryCard label="This Week" value={thisWeekCount} color="#d4edda" />
          <SummaryCard label="Most Active Region" value={REGION_LABELS[mostActiveRegion] || mostActiveRegion || 'N/A'} />
          <SummaryCard label="Latest Report" value={latestDate ? formatDate(latestDate) : 'N/A'} />
        </div>

        {/* Scrape Info + Button */}
        <div className="row mb-3 align-items-center">
          <div className="col-md-6">
            <span className="text-muted" style={{ fontSize: '0.85em' }}>
              Last scraped: {lastScraped ? formatTimestamp(lastScraped) : 'Never'}
            </span>
          </div>
          <div className="col-md-6 text-end">
            <button className="btn btn-sm btn-success me-2" onClick={handleScrape} disabled={scraping}>
              {scraping ? 'Scraping...' : 'Scrape Now'}
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setShowLog(!showLog); if (!showLog) loadScrapeLog(); }}>
              {showLog ? 'Hide Log' : 'Scrape Log'}
            </button>
          </div>
        </div>

        {/* Scrape Log */}
        {showLog && (
          <div className="card mb-3">
            <div className="card-header"><strong>Scrape History</strong></div>
            <div className="card-body" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table className="table table-sm table-bordered mb-0">
                <thead><tr><th>Time</th><th>Found</th><th>New</th><th>Status</th><th>Duration</th></tr></thead>
                <tbody>
                  {scrapeLogs.map((log, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: '0.8em' }}>{formatTimestamp(log.scraped_at)}</td>
                      <td>{log.rows_found}</td>
                      <td>{log.rows_inserted}</td>
                      <td>
                        <span style={{
                          color: log.status === 'success' ? '#198754' : '#dc3545',
                          fontWeight: 'bold', fontSize: '0.85em',
                        }}>
                          {log.status === 'success' ? 'OK' : 'FAIL'}
                        </span>
                        {log.error_message && <div style={{ fontSize: '0.7em', color: '#dc3545' }}>{log.error_message}</div>}
                      </td>
                      <td style={{ fontSize: '0.8em' }}>{log.duration_ms ? `${log.duration_ms}ms` : '—'}</td>
                    </tr>
                  ))}
                  {scrapeLogs.length === 0 && <tr><td colSpan="5" className="text-muted text-center">No scrape runs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="row mb-3 g-2">
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value })}>
              <option value="">All Regions</option>
              <option value="northeast">Northeast</option>
              <option value="southeast">Southeast</option>
              <option value="southwest">Southwest</option>
              <option value="northwest">Northwest</option>
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: e.target.value })}>
              <option value="7">This Week</option>
              <option value="14">Last 2 Weeks</option>
              <option value="30">Last Month</option>
              <option value="">All Time</option>
            </select>
          </div>
          <div className="col-md-4">
            <div className="input-group input-group-sm">
              <input type="text" className="form-control" placeholder="Search water body..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-outline-secondary" onClick={handleSearch}>Search</button>
            </div>
          </div>
          <div className="col-md-4">
            <span className="text-muted" style={{ fontSize: '0.85em', lineHeight: '31px' }}>
              {loading ? 'Loading...' : `Showing ${reports.length} of ${totalCount} entries`}
            </span>
          </div>
        </div>

        {/* Empty state */}
        {!loading && reports.length === 0 && (
          <div className="alert alert-info text-center">
            No stocking data yet. Click <strong>Scrape Now</strong> to pull the latest CPW stocking report.
          </div>
        )}

        {/* Table */}
        {reports.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-hover table-bordered table-striped table-sm">
              <thead className="table-secondary">
                <tr>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer', width: '250px' }} onClick={() => handleSort('body_of_water')}>
                    Body of Water{sortArrow('body_of_water')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer', width: '80px' }} onClick={() => handleSort('region')}>
                    Region{sortArrow('region')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer', width: '130px' }} onClick={() => handleSort('report_date')}>
                    Report Date{sortArrow('report_date')}
                  </th>
                  <th style={{ ...CELL_STYLE, width: '60px' }}>Atlas</th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer', width: '100px' }} onClick={() => handleSort('days_ago')}>
                    Days Ago{sortArrow('days_ago')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedReports().map((r) => (
                  <tr key={r.id} style={r.days_ago <= 7 ? { backgroundColor: '#d4edda' } : {}}>
                    <td style={{ ...CELL_STYLE, fontWeight: r.days_ago <= 7 ? 'bold' : 'normal' }}>
                      {r.body_of_water}
                    </td>
                    <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                      <RegionBadge region={r.region} />
                    </td>
                    <td style={CELL_STYLE}>{formatDate(r.report_date)}</td>
                    <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                      {r.atlas_url ? (
                        <a href={r.atlas_url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '0.85em' }} title="View on Colorado Fishing Atlas">
                          View
                        </a>
                      ) : '—'}
                    </td>
                    <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                      <span style={{
                        color: r.days_ago <= 3 ? '#198754' : r.days_ago <= 7 ? '#fd7e14' : '#6c757d',
                        fontWeight: r.days_ago <= 3 ? 'bold' : 'normal',
                      }}>
                        {r.days_ago_text}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Attribution */}
        <div className="text-center mt-4" style={{ fontSize: '0.75em', color: '#999' }}>
          Data source: Colorado Parks and Wildlife (cpw.state.co.us). This data is scraped from the public CPW Fish Stocking Report and is not affiliated with or endorsed by CPW.
        </div>
      </div>
    </div>
  );
};

export default FishStocking;
